import React from 'react';
import {Avatar, Box, Typography} from '@mui/material';
import {
    ChatBubbleOutline,
    FavoriteBorder,
    Visibility,
    TextSnippet,
    PlayCircleOutline,
} from '@mui/icons-material';
import {Link as RouterLink} from 'react-router-dom';
import {useCreatePath, useTranslate} from 'react-admin';
import moment from 'moment';
import {t} from '../themes/statamicTokens';
import StatusPill from '../components/statamic/StatusPill';

const statusPillVariant = (status) => {
    switch (status) {
        case 'PUBLISHED':        return 'success';
        case 'PENDING_APPROVAL': return 'warning';
        case 'REJECTED':         return 'danger';
        default:                 return 'neutral';
    }
};

const statusPillLabel = (status, translate) => {
    switch (status) {
        case 'PUBLISHED':        return translate('Published');
        case 'PENDING_APPROVAL': return translate('Pending Approval');
        case 'REJECTED':         return translate('Rejected');
        default:                 return status ?? '';
    }
};

/**
 * Shared post card — matches the PostListStatamic grid tile. Used on the
 * Posts list, the dashboard "Latest posts" widget, and anywhere else a
 * quick visual reference to a post is needed.
 *
 * Props:
 *   post         — the post record
 *   showStatus   — render status pill overlay on the media (list/dashboard when admin)
 *   showEngagement — render likes / comments / views row (default: true)
 *   showChild    — render the child avatar+name row (default: true)
 */
const PostCard = ({post, showStatus = false, showEngagement = true, showChild = true}) => {
    const createPath = useCreatePath();
    const translate = useTranslate();

    const isVideo = post.media_type === 'video' || post.message_type === 'VIDEO';
    const isPhoto = post.media_type === 'photo' || post.message_type === 'PHOTO';
    const isText  = post.message_type === 'TEXT';

    const mediaUrl = (isPhoto || isVideo)
        ? `${process.env.REACT_APP_CDN_URL}/${post.thumbnail}`
        : null;

    return (
        <Box
            component={RouterLink}
            to={createPath({resource: 'posts', id: post.id, type: 'show'})}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: t.color.surface,
                border: `1px solid ${t.color.border.default}`,
                borderRadius: `${t.radius.md}px`,
                overflow: 'hidden',
                textDecoration: 'none',
                color: 'inherit',
                transition: 'border-color 120ms ease, transform 120ms ease',
                '&:hover': {
                    borderColor: t.color.border.strong,
                    transform: 'translateY(-1px)',
                },
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '4 / 3',
                    backgroundColor: isText ? t.color.sidebarHover : '#000',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                }}
            >
                {isText && (
                    <Box sx={{p: 2.5, textAlign: 'center', color: t.color.text.primary}}>
                        <TextSnippet sx={{fontSize: 28, color: t.color.text.muted, mb: 1}}/>
                        <Typography
                            sx={{
                                fontSize: `${t.type.small.size}px`,
                                lineHeight: 1.5,
                                display: '-webkit-box',
                                WebkitLineClamp: 4,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        >
                            {post.description}
                        </Typography>
                    </Box>
                )}
                {mediaUrl && !isText && (
                    <Box
                        component="img"
                        src={mediaUrl}
                        alt={post.description || 'Post media'}
                        sx={{width: '100%', height: '100%', objectFit: 'cover'}}
                    />
                )}
                {isVideo && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(0,0,0,0.2)',
                        }}
                    >
                        <PlayCircleOutline sx={{fontSize: 56, color: 'rgba(255,255,255,0.95)'}}/>
                    </Box>
                )}
                {showStatus && post.status && (
                    <Box sx={{position: 'absolute', top: 8, right: 8}}>
                        <StatusPill variant={statusPillVariant(post.status)}>
                            {statusPillLabel(post.status, translate)}
                        </StatusPill>
                    </Box>
                )}
            </Box>

            <Box sx={{p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, flex: 1}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <Avatar
                        src={post.creator?.avatar_thumbnail
                            ? `${process.env.REACT_APP_CDN_URL}/${post.creator.avatar_thumbnail}`
                            : undefined}
                        sx={{
                            width: 28, height: 28,
                            bgcolor: t.color.sidebarHover,
                            color: t.color.text.secondary,
                            border: `1px solid ${t.color.border.hairline}`,
                            fontSize: 11,
                        }}
                    >
                        {post.creator?.given_name?.charAt(0)}
                    </Avatar>
                    <Box sx={{minWidth: 0, flex: 1}}>
                        <Typography
                            sx={{
                                fontSize: `${t.type.bodyStrong.size}px`,
                                fontWeight: 500,
                                color: t.color.text.primary,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {post.creator?.given_name}
                            {post.creator?.is_volunteer && post.child?.charity?.charity_name && (
                                <Box component="span" sx={{color: t.color.text.muted, fontWeight: 400}}>
                                    {' '}· {post.child.charity.charity_name}
                                </Box>
                            )}
                        </Typography>
                        <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.muted}}>
                            {moment(post.created_time).fromNow()}
                        </Typography>
                    </Box>
                </Box>

                {!isText && post.description && (
                    <Typography
                        sx={{
                            fontSize: `${t.type.small.size}px`,
                            color: t.color.text.primary,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            lineHeight: 1.4,
                        }}
                    >
                        {post.description}
                    </Typography>
                )}

                {showChild && post.child && (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mt: 'auto'}}>
                        <Avatar
                            src={post.child.profile_image_thumbnail
                                ? `${process.env.REACT_APP_CDN_URL}/${post.child.profile_image_thumbnail}`
                                : undefined}
                            sx={{
                                width: 20, height: 20,
                                bgcolor: t.color.sidebarHover,
                                border: `1px solid ${t.color.border.hairline}`,
                            }}
                        />
                        <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary}}>
                            {post.child.first_name} {post.child.last_name}
                        </Typography>
                    </Box>
                )}

                {showEngagement && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            pt: 1,
                            borderTop: `1px solid ${t.color.border.hairline}`,
                            color: t.color.text.muted,
                        }}
                    >
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                            <FavoriteBorder sx={{fontSize: 14}}/>
                            <Typography sx={{fontSize: `${t.type.caption.size}px`}}>{post.nr_of_likes ?? 0}</Typography>
                        </Box>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                            <ChatBubbleOutline sx={{fontSize: 14}}/>
                            <Typography sx={{fontSize: `${t.type.caption.size}px`}}>{post.nr_of_comments ?? 0}</Typography>
                        </Box>
                        {(post.views ?? 0) > 0 && (
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                <Visibility sx={{fontSize: 14}}/>
                                <Typography sx={{fontSize: `${t.type.caption.size}px`}}>{post.views}</Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default PostCard;
